import { useState } from "react";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { toast } from "sonner";
import Sidebar from "../components/Sidebar";
import { 
  ArrowLeft, 
  HelpCircle, 
  MessageSquare, 
  Mail, 
  Book, 
  ChevronDown, 
  ChevronUp,
  Search,
  Send,
  ExternalLink,
  Zap,
  Users,
  CreditCard,
  Shield,
  Briefcase,
  Video
} from "lucide-react";

const Support = () => {
  const [searchQuery, setSearchQuery] = useState("");
  const [expandedFaq, setExpandedFaq] = useState(null);
  const [contactForm, setContactForm] = useState({
    name: "",
    email: "",
    subject: "",
    message: ""
  });
  const [sending, setSending] = useState(false);

  const faqs = [
    {
      category: "Getting Started",
      icon: Zap,
      questions: [
        {
          q: "How do I create an account?",
          a: "Click 'Get Started' on the homepage and sign in with your Google account. Our secure OAuth integration will create your Rekrutor profile automatically. You can then choose whether you're a job seeker or recruiter."
        },
        {
          q: "How do I switch between job seeker and recruiter roles?",
          a: "Once you've selected a role, you can change it from your Profile Settings. Note that switching roles will reset some role-specific data. Contact support if you need to maintain both roles."
        },
        {
          q: "Is Rekrutor free to use?",
          a: "Yes! Rekrutor offers a free tier with core features including job browsing, profile creation, and basic AI interviews. Premium tiers (Plus and Pro) offer enhanced features like unlimited AI interviews, priority matching, and advanced analytics."
        }
      ]
    },
    {
      category: "For Job Seekers",
      icon: Users,
      questions: [
        {
          q: "How does the AI job matching work?",
          a: "Our AI analyzes your profile, skills, experience, and preferences to match you with relevant opportunities. The match percentage indicates how well your profile aligns with job requirements. The more complete your profile, the better the matches."
        },
        {
          q: "What is an AI Interview?",
          a: "AI Interviews are interactive sessions where our AI interviewer asks you relevant questions based on the job you're applying for. You can respond via text or voice. The AI provides feedback and insights to help you improve. Recruiters can view your interview performance."
        },
        {
          q: "How do I improve my match score?",
          a: "Complete your profile fully, add all relevant skills, upload your resume, and keep your work history updated. Our 'For You' page also recommends courses to help you learn missing skills that are in demand."
        },
        {
          q: "Can I apply to jobs anonymously?",
          a: "Your basic profile is visible to recruiters when you apply. However, contact information is only shared after mutual interest. You can control visibility settings in your Privacy preferences."
        }
      ]
    },
    {
      category: "For Recruiters",
      icon: Briefcase,
      questions: [
        {
          q: "How do I post a job?",
          a: "Navigate to 'Post Job' from your dashboard. Our AI can help generate a compelling job description, or you can write your own. Fill in the requirements, skills, salary range, and other details, then publish."
        },
        {
          q: "How does candidate ranking work?",
          a: "Candidates are ranked based on skill match, experience alignment, and AI interview performance (if completed). You can filter and sort candidates by various criteria and compare multiple profiles side-by-side."
        },
        {
          q: "Can I send interview invitations?",
          a: "Yes! From the candidate's profile or application, click 'Invite to Interview' to send an AI interview invitation. You can customize the questions and set a deadline. You'll be notified when they complete the interview."
        },
        {
          q: "How do I contact candidates?",
          a: "Use our built-in messaging system to communicate with candidates. For premium tiers, you can also schedule video calls directly through the platform with Google Calendar integration."
        }
      ]
    },
    {
      category: "Billing & Subscriptions",
      icon: CreditCard,
      questions: [
        {
          q: "What payment methods do you accept?",
          a: "We accept all major credit cards (Visa, Mastercard, American Express) processed securely through Stripe. We also support some regional payment methods depending on your location."
        },
        {
          q: "How do I cancel my subscription?",
          a: "Go to Settings > Subscription and click 'Cancel Subscription'. You'll retain access until the end of your billing period. No refunds are provided for partial months."
        },
        {
          q: "Can I get a refund?",
          a: "Annual subscriptions may qualify for a prorated refund within 14 days of purchase. Monthly subscriptions are non-refundable. Contact support for special circumstances."
        },
        {
          q: "What happens when my subscription expires?",
          a: "Your account reverts to the free tier. Your data is preserved, but you'll lose access to premium features like unlimited AI interviews and advanced analytics."
        }
      ]
    },
    {
      category: "Privacy & Security",
      icon: Shield,
      questions: [
        {
          q: "How is my data protected?",
          a: "We use industry-standard encryption (TLS/SSL) for all data transmission. Your data is stored securely with access controls. We never sell your personal information. See our Privacy Policy for details."
        },
        {
          q: "Can I delete my account and data?",
          a: "Yes. Go to Settings > Account > Delete Account. This permanently removes your profile, applications, and associated data. This action cannot be undone."
        },
        {
          q: "Who can see my profile?",
          a: "Job seekers' profiles are visible to recruiters when applying or if profile visibility is set to public. Recruiters' profiles are visible on their job postings. You can adjust visibility in Privacy settings."
        }
      ]
    },
    {
      category: "Technical Issues",
      icon: Video,
      questions: [
        {
          q: "The AI interview isn't loading. What should I do?",
          a: "Ensure you're using a modern browser (Chrome, Firefox, Safari, Edge). Check your internet connection and try clearing your browser cache. If using voice mode, ensure microphone permissions are granted."
        },
        {
          q: "I'm not receiving email notifications.",
          a: "Check your spam/junk folder first. Add noreply@rekrutor.com to your contacts. Verify your email is correct in Settings. You can also check Notification Settings to ensure emails are enabled."
        },
        {
          q: "Video calls aren't working.",
          a: "Ensure you've granted camera and microphone permissions in your browser. Check that you're not in a restricted network. Try refreshing the page or using a different browser."
        }
      ]
    }
  ];

  const filteredFaqs = searchQuery
    ? faqs.map(category => ({
        ...category,
        questions: category.questions.filter(
          q => q.q.toLowerCase().includes(searchQuery.toLowerCase()) ||
               q.a.toLowerCase().includes(searchQuery.toLowerCase())
        )
      })).filter(category => category.questions.length > 0)
    : faqs;

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!contactForm.name || !contactForm.email || !contactForm.message) {
      toast.error("Please fill in all required fields");
      return;
    }
    
    setSending(true);
    // Simulate sending (in production, this would call an API)
    await new Promise(resolve => setTimeout(resolve, 1500));
    toast.success("Message sent! We'll get back to you within 24-48 hours.");
    setContactForm({ name: "", email: "", subject: "", message: "" });
    setSending(false);
  };

  return (
    <div className="min-h-screen bg-obsidian">
      <Sidebar />
      
      <main className="lg:ml-64 min-h-screen p-6 pt-16 lg:pt-6">
        <div className="max-w-5xl mx-auto">
          {/* Page Header */}
          <div className="mb-8">
            <h1 className="font-heading text-3xl font-bold mb-2">
              SUPPORT <span className="text-crimson">CENTER</span>
            </h1>
            <p className="text-gray-400">Search our FAQ or contact us directly</p>
          </div>
          
          {/* Search Bar */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            className="mb-8"
          >
            <div className="relative">
              <Search className="absolute left-4 top-1/2 -translate-y-1/2 w-5 h-5 text-gray-500" />
              <Input
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                placeholder="Search for answers..."
                className="pl-12 py-6 bg-obsidian-light border-[#333] text-lg"
                data-testid="faq-search"
              />
            </div>
          </motion.div>

          {/* Quick Links */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.1 }}
            className="grid grid-cols-1 sm:grid-cols-3 gap-4 mb-12"
          >
            <a 
              href="mailto:support@rekrutor.com"
              className="flex items-center gap-4 p-4 bg-obsidian-light border border-[#222] hover:border-crimson/50 transition-colors"
            >
              <div className="w-12 h-12 bg-crimson/20 flex items-center justify-center">
                <Mail className="w-6 h-6 text-crimson" />
              </div>
              <div>
                <h3 className="font-medium text-white">Email Support</h3>
                <p className="text-gray-500 text-sm">support@rekrutor.com</p>
              </div>
            </a>
            <Link 
              to="/community"
              className="flex items-center gap-4 p-4 bg-obsidian-light border border-[#222] hover:border-crimson/50 transition-colors"
            >
              <div className="w-12 h-12 bg-purple-500/20 flex items-center justify-center">
                <MessageSquare className="w-6 h-6 text-purple-400" />
              </div>
              <div>
                <h3 className="font-medium text-white">Community Forum</h3>
                <p className="text-gray-500 text-sm">Ask the community</p>
              </div>
            </Link>
            <a 
              href="#contact-form"
              className="flex items-center gap-4 p-4 bg-obsidian-light border border-[#222] hover:border-crimson/50 transition-colors"
            >
              <div className="w-12 h-12 bg-green-500/20 flex items-center justify-center">
                <Send className="w-6 h-6 text-green-400" />
              </div>
              <div>
                <h3 className="font-medium text-white">Contact Form</h3>
                <p className="text-gray-500 text-sm">Send us a message</p>
              </div>
            </a>
          </motion.div>

          {/* FAQ Section */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.2 }}
            className="mb-16"
          >
            <div className="flex items-center gap-2 mb-6">
              <Book className="w-5 h-5 text-crimson" />
              <h2 className="font-heading text-xl font-bold">Frequently Asked Questions</h2>
            </div>

            {filteredFaqs.length === 0 ? (
              <div className="text-center py-12 bg-obsidian-light border border-[#222]">
                <HelpCircle className="w-12 h-12 text-gray-600 mx-auto mb-4" />
                <p className="text-gray-400">No results found for "{searchQuery}"</p>
                <p className="text-gray-500 text-sm mt-2">Try a different search term or contact us directly</p>
              </div>
            ) : (
              <div className="space-y-6">
                {filteredFaqs.map((category, catIndex) => (
                  <div key={category.category} className="bg-obsidian-light border border-[#222]">
                    <div className="flex items-center gap-3 p-4 border-b border-[#333]">
                      <category.icon className="w-5 h-5 text-crimson" />
                      <h3 className="font-heading font-bold">{category.category}</h3>
                    </div>
                    <div className="divide-y divide-[#333]">
                      {category.questions.map((faq, qIndex) => {
                        const faqId = `${catIndex}-${qIndex}`;
                        const isExpanded = expandedFaq === faqId;
                        
                        return (
                          <div key={qIndex}>
                            <button
                              onClick={() => setExpandedFaq(isExpanded ? null : faqId)}
                              className="w-full flex items-center justify-between p-4 text-left hover:bg-obsidian/50 transition-colors"
                              data-testid={`faq-${catIndex}-${qIndex}`}
                            >
                              <span className="text-white font-medium pr-4">{faq.q}</span>
                              {isExpanded ? (
                                <ChevronUp className="w-5 h-5 text-gray-500 flex-shrink-0" />
                              ) : (
                                <ChevronDown className="w-5 h-5 text-gray-500 flex-shrink-0" />
                              )}
                            </button>
                            {isExpanded && (
                              <motion.div
                                initial={{ opacity: 0, height: 0 }}
                                animate={{ opacity: 1, height: "auto" }}
                                className="px-4 pb-4"
                              >
                                <p className="text-gray-400 leading-relaxed">{faq.a}</p>
                              </motion.div>
                            )}
                          </div>
                        );
                      })}
                    </div>
                  </div>
                ))}
              </div>
            )}
          </motion.div>

          {/* Contact Form */}
          <motion.div
            id="contact-form"
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.3 }}
            className="bg-obsidian-light border border-[#222] p-8"
          >
            <div className="flex items-center gap-2 mb-6">
              <MessageSquare className="w-5 h-5 text-crimson" />
              <h2 className="font-heading text-xl font-bold">Contact Us</h2>
            </div>
            <p className="text-gray-400 mb-6">
              Can't find what you're looking for? Send us a message and we'll get back to you within 24-48 hours.
            </p>

            <form onSubmit={handleSubmit} className="space-y-4">
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <div>
                  <label className="text-sm text-gray-400 block mb-2">Name *</label>
                  <Input
                    value={contactForm.name}
                    onChange={(e) => setContactForm({ ...contactForm, name: e.target.value })}
                    placeholder="Your name"
                    className="bg-obsidian border-[#333]"
                    data-testid="contact-name"
                  />
                </div>
                <div>
                  <label className="text-sm text-gray-400 block mb-2">Email *</label>
                  <Input
                    type="email"
                    value={contactForm.email}
                    onChange={(e) => setContactForm({ ...contactForm, email: e.target.value })}
                    placeholder="your@email.com"
                    className="bg-obsidian border-[#333]"
                    data-testid="contact-email"
                  />
                </div>
              </div>
              <div>
                <label className="text-sm text-gray-400 block mb-2">Subject</label>
                <Input
                  value={contactForm.subject}
                  onChange={(e) => setContactForm({ ...contactForm, subject: e.target.value })}
                  placeholder="What's this about?"
                  className="bg-obsidian border-[#333]"
                  data-testid="contact-subject"
                />
              </div>
              <div>
                <label className="text-sm text-gray-400 block mb-2">Message *</label>
                <textarea
                  value={contactForm.message}
                  onChange={(e) => setContactForm({ ...contactForm, message: e.target.value })}
                  placeholder="How can we help you?"
                  rows={5}
                  className="w-full px-4 py-3 bg-obsidian border border-[#333] text-white focus:border-crimson focus:outline-none resize-none"
                  data-testid="contact-message"
                />
              </div>
              <Button
                type="submit"
                disabled={sending}
                className="bg-crimson hover:bg-crimson/90 text-white"
                data-testid="contact-submit"
              >
                {sending ? (
                  <>
                    <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin mr-2" />
                    Sending...
                  </>
                ) : (
                  <>
                    <Send className="w-4 h-4 mr-2" />
                    Send Message
                  </>
                )}
              </Button>
            </form>
          </motion.div>
        </div>
      </main>

      {/* Footer */}
      <footer className="border-t border-[#222] py-6">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 flex justify-between items-center">
          <p className="text-gray-500 text-sm">© 2025 Rekrutor. All rights reserved.</p>
          <div className="flex gap-4 text-sm">
            <Link to="/privacy" className="text-gray-400 hover:text-crimson">Privacy</Link>
            <Link to="/terms" className="text-gray-400 hover:text-crimson">Terms</Link>
          </div>
        </div>
      </footer>
    </div>
  );
};

export default Support;
